import { useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { useLocalization } from "../../../hooks/useLocalization";
import { useMatches } from "../../../hooks/useMatches";
import { useTeamInfo } from "../../../hooks/useTeams";
import { fetchSeasonStatsMeta, fetchTeamScores } from "../../../services/services";
import { IMatch, ITeamScores } from "../../../types/types";
import { sortSeasonsDesc } from "../../../utils/seasonOrder";

const matchIncludesTeam = (match: IMatch, teamIds: Set<string>) => {
  if (!teamIds.size) return true;
  return teamIds.has(match.teamOne) || teamIds.has(match.teamTwo);
};

export const useResults = () => {
  const { strings } = useLocalization();
  const { playedMatches, loading: matchesLoading } = useMatches();
  const teamInfos = useTeamInfo();
  const [currentSeason, setCurrentSeason] = useState("");
  const [selectedSeason, setSelectedSeason] = useState("");
  const [standings, setStandings] = useState<ITeamScores[]>([]);
  const [standingsLoading, setStandingsLoading] = useState(true);
  const [selectedTeamIds, setSelectedTeamIds] = useState<Set<string>>(() => new Set());
  const deferredTeamIds = useDeferredValue(selectedTeamIds);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    void (async () => {
      try {
        const meta = await fetchSeasonStatsMeta();
        if (!mountedRef.current) return;
        setCurrentSeason(meta.currentSeason);
      } catch (error) {
        console.error("Failed to load season metadata:", error);
      }
    })();

    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!selectedSeason) {
      setStandings([]);
      setStandingsLoading(false);
      return;
    }

    mountedRef.current = true;
    setStandingsLoading(true);

    void (async () => {
      try {
        const teams = await fetchTeamScores(selectedSeason);
        if (!mountedRef.current) return;
        setStandings(teams);
      } catch (error) {
        console.error("Failed to load standings:", error);
        if (!mountedRef.current) return;
        setStandings([]);
      } finally {
        if (mountedRef.current) setStandingsLoading(false);
      }
    })();

    return () => {
      mountedRef.current = false;
    };
  }, [selectedSeason]);

  const seasonOptions = useMemo(() => {
    const fromMatches =
      playedMatches?.map((match) => match.season).filter((season): season is string => !!season) ??
      [];
    return sortSeasonsDesc([...new Set(fromMatches)]);
  }, [playedMatches]);

  useEffect(() => {
    if (!seasonOptions.length) {
      setSelectedSeason("");
      return;
    }

    setSelectedSeason((current) => {
      if (current && seasonOptions.includes(current)) return current;
      if (currentSeason && seasonOptions.includes(currentSeason)) return currentSeason;
      return seasonOptions[0];
    });
  }, [seasonOptions, currentSeason]);

  const seasonMatches = useMemo(() => {
    if (!playedMatches?.length || !selectedSeason) return [];
    return playedMatches.filter((match) => match.season === selectedSeason);
  }, [playedMatches, selectedSeason]);

  const teamsInResults = useMemo(() => {
    if (!teamInfos?.length || !seasonMatches.length) return [];

    const ids = new Set<string>();
    seasonMatches.forEach((match) => {
      if (match.teamOne) ids.add(match.teamOne);
      if (match.teamTwo) ids.add(match.teamTwo);
    });

    return teamInfos
      .filter((team) => ids.has(team.id))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
  }, [teamInfos, seasonMatches]);

  const filteredMatches = useMemo(() => {
    return seasonMatches
      .filter((match) => matchIncludesTeam(match, deferredTeamIds))
      .sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime());
  }, [seasonMatches, deferredTeamIds]);

  const toggleTeam = (teamId: string) => {
    setSelectedTeamIds((prev) => {
      const next = new Set(prev);
      if (next.has(teamId)) {
        next.delete(teamId);
      } else {
        next.add(teamId);
      }
      return next;
    });
  };

  const clearTeamFilter = () => {
    setSelectedTeamIds(new Set());
  };

  const loading = matchesLoading || standingsLoading;
  const hasSeasonMatches = seasonMatches.length > 0;
  const teamFilterActive = deferredTeamIds.size > 0;
  const showFilters = !loading && !!selectedSeason;

  return {
    strings,
    currentSeason,
    selectedSeason,
    setSelectedSeason,
    standings,
    selectedTeamIds,
    seasonOptions,
    teamsInResults,
    filteredMatches,
    toggleTeam,
    clearTeamFilter,
    loading,
    hasSeasonMatches,
    teamFilterActive,
    showFilters,
  };
};
